import { ENDPOINT } from '~/constants';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type DismissParsingCaseMutationResponse,
  type DismissParsingCaseVariables,
} from '../types';
import { zodDismissParsingCaseResponseSchema } from '../validations';

const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  404: () =>
    i18n.t(($) => $.dismissParsingCase.error404, {
      ns: 'data',
    }),
  422: () =>
    i18n.t(($) => $.dismissParsingCase.error422, {
      ns: 'data',
    }),
};

/**
 * Dismiss a parsing case. Sets the case status to DISMISSED and records
 * `dismissedOn` / `dismissedBy`. An optional `reason` is stored on the case
 * meta. Allowed regardless of the current case status.
 *
 * @param variables - The case UUID and an optional dismissal reason
 */
export const dismissParsingCase = async ({
  caseId,
  reason,
}: DismissParsingCaseVariables): Promise<DismissParsingCaseMutationResponse> => {
  try {
    return await vestigasApi
      .post(ENDPOINT.PARSING_CASE.DISMISS(caseId), {
        ...(reason ? { json: { reason } } : {}),
        validate: { response: zodDismissParsingCaseResponseSchema },
      })
      .json<DismissParsingCaseMutationResponse>();
  } catch (error) {
    log.error({
      message: 'Error dismissing parsing case',
      error,
    });

    const errorMessage = resolveMutationErrorMessage(
      error,
      ERROR_MESSAGE_BY_STATUS,
      () =>
        i18n.t(($) => $.dismissParsingCase.error.generic, {
          ns: 'data',
        }),
    );
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
